/**
 * Planned series / occurrences / coverage API (TMMAPP-0009 / TMMAPP-0011, design §8.9 / §10) —
 * wire types from `src/api/generated/`; normalized into the module's domain type. Match /
 * unmatch / post-now return `JsonNode` and are not parsed; the caller refetches the calendar
 * window after every 2xx write. `Idempotency-Key` is minted once per submit attempt on match /
 * unmatch / post-now.
 */
import { request, requestMeta } from './client';
import { personNameOrNull } from './personNames';
import { ApiShapeError } from './errors';
import type {
  CoverageResponse as WireCoverage,
  ExceptionPreview as WireExceptionPreview,
  MatchSettingsWrite,
  PlannedSeriesCreateRequest,
  PlannedSeriesPatchRequest,
  PlannedOccurrenceView as WireOccurrence,
  Recurrence as WireRecurrence,
  SeriesResponse as WireSeries,
  SliceView as WireSliceView,
  TargetRequest as WireTargetRequest,
  TargetView as WireTargetView,
  UnmatchRequest,
} from './generated';

export type Frequency = 'ONCE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
export type Weekday = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';
export type WeekOrdinal = 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH' | 'LAST';

export interface RecurrenceRule {
  frequency: Frequency;
  interval: number | null;
  byWeekdays: readonly Weekday[] | null;
  byMonthDays: readonly number[] | null;
  byWeekOrdinal: WeekOrdinal | null;
  byOrdinalWeekday: Weekday | null;
  byMonth: number | null;
  startOn: string;
  endsOn: string | null;
  occurrenceCount: number | null;
}

export type SliceMode = 'FIXED' | 'PERCENT' | 'REMAINDER';

export interface TargetRequest {
  seriesTargetId?: number | undefined;
  envelopeId: number;
  mode: SliceMode;
  fixedAmountMinor?: number | undefined;
  percentBp?: number | undefined;
  categoryId?: number | undefined;
}

export interface TargetView {
  id: number;
  envelopeId: number;
  envelopeName: string | null;
  /** The envelope's virtual account (its route); null when not served. */
  virtualAccountId: number | null;
  virtualAccountLabel: string | null;
  envelopeOwnerDisplayName: string | null;
  mode: SliceMode;
  fixedAmountMinor: number | null;
  percentBp: number | null;
  categoryId: number | null;
}

export interface MatchSettings {
  payeeContains: string | null;
  amountToleranceBp: number | null;
  windowDays: number | null;
}

export type Fulfilment = 'EXPECTED' | 'AUTO_POST';
export type SeriesStatus = 'ACTIVE' | 'ENDED' | 'CANCELLED';

export interface ExceptionRef {
  originalOn: string;
}

export interface DroppedException {
  originalOn: string;
  reason: 'RESET' | 'ORPHANED';
}

export interface Series {
  id: number;
  accountId: number;
  name: string;
  payee: string | null;
  kind: 'INCOME' | 'EXPENSE';
  amountMinor: number;
  recurrence: RecurrenceRule;
  fulfilment: Fulfilment;
  match: MatchSettings;
  authorDisplayName: string;
  status: SeriesStatus;
  splitFromSeriesId: number | null;
  targets: TargetView[];
  exceptionsKept: ExceptionRef[] | null;
  exceptionsDropped: DroppedException[] | null;
  /**
   * The next expected occurrence on or after today (TMMAPI-0186 U3), served on the series list only;
   * null when there's none (an ended or cancelled series, nothing ahead) or not served.
   */
  nextOccursOn: string | null;
}

export interface SeriesCreate {
  accountId: number;
  name: string;
  payee?: string | undefined;
  kind: 'INCOME' | 'EXPENSE';
  amountMinor: number;
  recurrence: RecurrenceRule;
  fulfilment?: Fulfilment | undefined;
  match?: MatchSettings | undefined;
  targets?: TargetRequest[] | undefined;
}

export interface SeriesPatch extends Partial<Omit<SeriesCreate, 'accountId'>> {
  applyTo?: 'SERIES' | undefined;
  exceptions?: 'KEEP' | 'RESET' | undefined;
}

export type OccurrenceState =
  'PLANNED' | 'PARTIAL' | 'MATCHED' | 'POSTED' | 'MISSED' | 'SUPERSEDED' | 'CANCELLED';

export interface Slice {
  sliceRef: string;
  envelopeId: number;
  envelopeName: string | null;
  virtualAccountLabel: string | null;
  amountMinor: number;
  categoryId: number | null;
  /**
   * What the part holds from this occurrence (TMMAPI-0202, MM-D-153; owner-private): null before any
   * line is matched, or when not served. Its `amountMinor` is what it is owed in full.
   */
  receivedMinor: number | null;
}

export interface OccurrenceDiffers {
  date: boolean;
  amount: boolean;
  slices: boolean;
}

export interface Occurrence {
  seriesId: number;
  originalOn: string;
  occursOn: string;
  amountMinor: number;
  payee: string | null;
  state: OccurrenceState;
  transactionId: number | null;
  differsFromSeries: OccurrenceDiffers | null;
  slices: Slice[];
  remainderMinor: number;
  partiallyExecuted: boolean | null;
  pending: boolean | null;
  /**
   * A paycheck in parts (TMMAPI-0202, MM-D-153), owner-private and served only when the viewer sees
   * the slices; null (lines `[]`) when not served. tmm-api works every figure out; the app renders them.
   */
  receivedMinor: number | null;
  outstandingMinor: number | null;
  /** What arrived past the plan that no part took; null when zero (absent). */
  extraMinor: number | null;
  /** A person closed it short (`…/close`). */
  closedShort: boolean;
  /** The bank lines matched to it, oldest first. */
  lines: OccurrenceLine[];
}

export interface OccurrenceLine {
  transactionId: number;
  postedOn: string;
  amountMinor: number;
}

export interface ExceptionDiffers {
  date: boolean;
  amount: boolean;
  payee: boolean;
  slices: boolean;
}

export interface ExceptionPreview {
  originalOn: string;
  occursOn: string;
  state: OccurrenceState;
  differs: ExceptionDiffers;
  wouldOrphanUnder: boolean | null;
}

export interface CoverageResult {
  coveredThrough: string;
  superseded: number;
  missed: number;
}

const FREQUENCIES: ReadonlySet<string> = new Set([
  'ONCE',
  'DAILY',
  'WEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'YEARLY',
]);
const WEEKDAYS: ReadonlySet<string> = new Set(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']);
const ORDINALS: ReadonlySet<string> = new Set(['FIRST', 'SECOND', 'THIRD', 'FOURTH', 'LAST']);
const SLICE_MODES: ReadonlySet<string> = new Set(['FIXED', 'PERCENT', 'REMAINDER']);
const STATES: ReadonlySet<string> = new Set([
  'PLANNED',
  'PARTIAL',
  'MATCHED',
  'POSTED',
  'MISSED',
  'SUPERSEDED',
  'CANCELLED',
]);

/** The known weekdays in `value`, or `null` when none (an unknown future value is skipped). */
function weekdayList(value: readonly string[] | undefined): Weekday[] | null {
  const days = (value ?? []).filter((d): d is Weekday => WEEKDAYS.has(d));
  return days.length > 0 ? days : null;
}

/** A rule; `null` for a frequency this app doesn't know yet. */
export function normalizeRecurrence(w: WireRecurrence): RecurrenceRule | null {
  if (!FREQUENCIES.has(w.frequency)) return null;
  return {
    frequency: w.frequency,
    interval: w.interval ?? null,
    byWeekdays: weekdayList(w.byWeekdays),
    byMonthDays: w.byMonthDays != null && w.byMonthDays.length > 0 ? w.byMonthDays : null,
    byWeekOrdinal:
      w.byWeekOrdinal !== undefined && ORDINALS.has(w.byWeekOrdinal) ? w.byWeekOrdinal : null,
    byOrdinalWeekday:
      w.byOrdinalWeekday !== undefined && WEEKDAYS.has(w.byOrdinalWeekday)
        ? (w.byOrdinalWeekday as Weekday)
        : null,
    byMonth: w.byMonth ?? null,
    startOn: w.startOn,
    endsOn: w.endsOn ?? null,
    occurrenceCount: w.occurrenceCount ?? null,
  };
}

/** A target; `null` for one without an envelope (this app reads envelope targets only). */
function normalizeTarget(w: WireTargetView): TargetView | null {
  if (w.envelopeId == null) return null;
  return {
    id: w.id,
    envelopeId: w.envelopeId,
    envelopeName: w.envelopeName ?? null,
    virtualAccountId: w.virtualAccountId ?? null,
    virtualAccountLabel: w.virtualAccountLabel ?? null,
    envelopeOwnerDisplayName: personNameOrNull(w.envelopeOwnerDisplayName),
    mode: SLICE_MODES.has(w.mode) ? w.mode : 'FIXED',
    fixedAmountMinor: w.fixedAmountMinor ?? null,
    percentBp: w.percentBp ?? null,
    categoryId: w.categoryId ?? null,
  };
}

/**
 * A series row; `null` (dropped, warned in DEV) when its recurrence frequency is one this app
 * doesn't know yet.
 */
export function normalizeSeries(w: WireSeries): Series | null {
  const recurrence = normalizeRecurrence(w.recurrence);
  if (!recurrence) {
    if (import.meta.env.DEV)
      console.warn('planned: series with an unknown recurrence dropped', w.id);
    return null;
  }
  return {
    id: w.id,
    accountId: w.accountId,
    name: w.name,
    payee: w.payee ?? null,
    kind: w.kind === 'INCOME' ? 'INCOME' : 'EXPENSE',
    amountMinor: w.amountMinor,
    recurrence,
    fulfilment: w.fulfilment === 'AUTO_POST' ? 'AUTO_POST' : 'EXPECTED',
    match: {
      payeeContains: w.match.payeeContains,
      amountToleranceBp: w.match.amountToleranceBp,
      windowDays: w.match.windowDays,
    },
    authorDisplayName: w.authorDisplayName ?? '',
    status: w.status === 'ENDED' ? 'ENDED' : w.status === 'CANCELLED' ? 'CANCELLED' : 'ACTIVE',
    splitFromSeriesId: w.splitFromSeriesId ?? null,
    targets: w.targets.flatMap((row) => {
      const t = normalizeTarget(row);
      return t ? [t] : [];
    }),
    exceptionsKept: w.exceptionsKept?.map((row) => ({ originalOn: row.originalOn })) ?? null,
    exceptionsDropped:
      w.exceptionsDropped?.map((row) => ({
        originalOn: row.originalOn,
        reason: row.reason === 'ORPHANED' ? 'ORPHANED' : 'RESET',
      })) ?? null,
    nextOccursOn: w.nextOccursOn ?? null,
  };
}

export function normalizeSeriesList(value: readonly WireSeries[]): Series[] {
  return value.flatMap((row) => {
    const series = normalizeSeries(row);
    return series ? [series] : [];
  });
}

/** A slice; `null` for one without an envelope (this app reads envelope slices only). */
function normalizeSlice(w: WireSliceView): Slice | null {
  if (w.envelopeId == null) return null;
  return {
    sliceRef: w.sliceRef,
    envelopeId: w.envelopeId,
    envelopeName: w.envelopeName ?? null,
    virtualAccountLabel: w.virtualAccountLabel ?? null,
    amountMinor: w.amountMinor,
    categoryId: w.categoryId ?? null,
    receivedMinor: w.receivedMinor ?? null,
  };
}

/** An occurrence. An unknown future state reads as PLANNED. */
export function normalizeOccurrence(w: WireOccurrence): Occurrence {
  return {
    seriesId: w.seriesId,
    originalOn: w.originalOn,
    occursOn: w.occursOn,
    amountMinor: w.amountMinor,
    payee: w.payee ?? null,
    state: STATES.has(w.state) ? w.state : 'PLANNED',
    transactionId: w.transactionId ?? null,
    differsFromSeries:
      w.differsFromSeries == null
        ? null
        : {
            date: w.differsFromSeries.date,
            amount: w.differsFromSeries.amount,
            slices: w.differsFromSeries.slices,
          },
    slices: w.slices.flatMap((row) => {
      const slice = normalizeSlice(row);
      return slice ? [slice] : [];
    }),
    remainderMinor: w.remainderMinor,
    partiallyExecuted: w.partiallyExecuted ?? null,
    pending: w.pending ?? null,
    receivedMinor: w.receivedMinor ?? null,
    outstandingMinor: w.outstandingMinor ?? null,
    extraMinor: w.extraMinor ?? null,
    closedShort: w.closedShort === true,
    lines: (w.lines ?? []).map((line) => ({
      transactionId: line.transactionId,
      postedOn: line.postedOn,
      amountMinor: line.amountMinor,
    })),
  };
}

/** The occurrences of a window and the window tmm-api resolved (TMMAPI-0188 U3). */
export interface ServedOccurrences {
  occurrences: Occurrence[];
  /** The served first day; null when not served. */
  windowFrom: string | null;
  /** The served last day (before `windowFrom` for a wholly past window); null when not served. */
  windowTo: string | null;
}

const CIVIL_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A served civil date, or null when absent or not one. */
function civilOrNull(value: string | null): string | null {
  return value !== null && CIVIL_DATE.test(value) ? value : null;
}

export function normalizeOccurrenceList(value: readonly WireOccurrence[]): Occurrence[] {
  return value.map(normalizeOccurrence);
}

export function normalizeExceptionPreview(w: WireExceptionPreview): ExceptionPreview {
  return {
    originalOn: w.originalOn,
    occursOn: w.occursOn,
    state: STATES.has(w.state) ? w.state : 'PLANNED',
    differs: {
      date: w.differs.date,
      amount: w.differs.amount,
      payee: w.differs.payee,
      slices: w.differs.slices,
    },
    wouldOrphanUnder: w.wouldOrphanUnder ?? null,
  };
}

export function normalizeExceptionPreviewList(
  value: readonly WireExceptionPreview[],
): ExceptionPreview[] {
  return value.map(normalizeExceptionPreview);
}

export function normalizeCoverage(w: WireCoverage): CoverageResult {
  return { coveredThrough: w.coveredThrough, superseded: w.superseded, missed: w.missed };
}

function requiredSeries(wire: WireSeries): Series {
  const series = normalizeSeries(wire);
  if (!series) throw new ApiShapeError('series.recurrence has an unknown frequency');
  return series;
}

/** The rule on the wire: an unset part is omitted, never sent as `null` (TMMAPI-0167). */
export function toWireRecurrence(rule: RecurrenceRule): WireRecurrence {
  const w: WireRecurrence = { frequency: rule.frequency, startOn: rule.startOn };
  if (rule.interval !== null) w.interval = rule.interval;
  if (rule.byWeekdays !== null) w.byWeekdays = [...rule.byWeekdays];
  if (rule.byMonthDays !== null) w.byMonthDays = [...rule.byMonthDays];
  if (rule.byWeekOrdinal !== null) w.byWeekOrdinal = rule.byWeekOrdinal;
  if (rule.byOrdinalWeekday !== null) w.byOrdinalWeekday = rule.byOrdinalWeekday;
  if (rule.byMonth !== null) w.byMonth = rule.byMonth;
  if (rule.endsOn !== null) w.endsOn = rule.endsOn;
  if (rule.occurrenceCount !== null) w.occurrenceCount = rule.occurrenceCount;
  return w;
}

function toWireMatch(match: MatchSettings): MatchSettingsWrite {
  const w: MatchSettingsWrite = {};
  if (match.payeeContains !== null) w.payeeContains = match.payeeContains;
  if (match.amountToleranceBp !== null) w.amountToleranceBp = match.amountToleranceBp;
  if (match.windowDays !== null) w.windowDays = match.windowDays;
  return w;
}

function toWireTarget(target: TargetRequest): WireTargetRequest {
  const w: WireTargetRequest = { envelopeId: target.envelopeId, mode: target.mode };
  if (target.seriesTargetId !== undefined) w.seriesTargetId = target.seriesTargetId;
  if (target.fixedAmountMinor !== undefined) w.fixedAmountMinor = target.fixedAmountMinor;
  if (target.percentBp !== undefined) w.percentBp = target.percentBp;
  if (target.categoryId !== undefined) w.categoryId = target.categoryId;
  return w;
}

/** A series PATCH (or a split's changes): only the keys the caller set; no key is sent as `null`. */
function seriesPatchBody(body: SeriesPatch): PlannedSeriesPatchRequest {
  const w: PlannedSeriesPatchRequest = {};
  if (body.name !== undefined) w.name = body.name;
  if (body.payee !== undefined) w.payee = body.payee;
  if (body.kind !== undefined) w.kind = body.kind;
  if (body.amountMinor !== undefined) w.amountMinor = body.amountMinor;
  if (body.recurrence !== undefined) w.recurrence = toWireRecurrence(body.recurrence);
  if (body.fulfilment !== undefined) w.fulfilment = body.fulfilment;
  if (body.match !== undefined) w.match = toWireMatch(body.match);
  if (body.targets !== undefined) w.targets = body.targets.map(toWireTarget);
  if (body.applyTo !== undefined) w.applyTo = body.applyTo;
  if (body.exceptions !== undefined) w.exceptions = body.exceptions;
  return w;
}

function seriesCreateBody(body: SeriesCreate): PlannedSeriesCreateRequest {
  const { accountId, name, kind, amountMinor, recurrence } = body;
  return {
    ...seriesPatchBody(body),
    accountId,
    name,
    kind,
    amountMinor,
    recurrence: toWireRecurrence(recurrence),
  };
}

/**
 * Flat query for `GET …/exceptions`. Spring `@ParameterObject Recurrence` binds dotted keys
 * (`recurrence.frequency`, …). Arrays are comma-joined because `request()` query values are
 * scalars. GET never has a body.
 */
export function recurrenceQuery(
  rule: Partial<RecurrenceRule>,
): Record<string, string | number | boolean | undefined> {
  const q: Record<string, string | number | boolean | undefined> = {};
  if (rule.frequency !== undefined) q['recurrence.frequency'] = rule.frequency;
  if (rule.interval != null) q['recurrence.interval'] = rule.interval;
  if (rule.byWeekdays != null && rule.byWeekdays.length > 0) {
    q['recurrence.byWeekdays'] = rule.byWeekdays.join(',');
  }
  if (rule.byMonthDays != null && rule.byMonthDays.length > 0) {
    q['recurrence.byMonthDays'] = rule.byMonthDays.join(',');
  }
  if (rule.byWeekOrdinal != null) q['recurrence.byWeekOrdinal'] = rule.byWeekOrdinal;
  if (rule.byOrdinalWeekday != null) q['recurrence.byOrdinalWeekday'] = rule.byOrdinalWeekday;
  if (rule.byMonth != null) q['recurrence.byMonth'] = rule.byMonth;
  if (rule.startOn !== undefined) q['recurrence.startOn'] = rule.startOn;
  if (rule.endsOn != null) q['recurrence.endsOn'] = rule.endsOn;
  if (rule.occurrenceCount != null) q['recurrence.occurrenceCount'] = rule.occurrenceCount;
  return q;
}

export const plannedApi = {
  /** GET /v1/me/planned-series. */
  async listSeries(
    q: { accountId?: number | undefined; status?: SeriesStatus | undefined } = {},
  ): Promise<Series[]> {
    return normalizeSeriesList(
      await request<WireSeries[]>('GET', '/v1/me/planned-series', {
        query: { accountId: q.accountId, status: q.status },
      }),
    );
  },
  /** POST /v1/me/planned-series. */
  async createSeries(body: SeriesCreate): Promise<Series> {
    return requiredSeries(
      await request<WireSeries>('POST', '/v1/me/planned-series', { json: seriesCreateBody(body) }),
    );
  },
  /** GET /v1/me/planned-series/{id}. */
  async getSeries(id: number): Promise<Series> {
    return requiredSeries(await request<WireSeries>('GET', `/v1/me/planned-series/${id}`));
  },
  /** PATCH /v1/me/planned-series/{id}. */
  async patchSeries(id: number, body: SeriesPatch): Promise<Series> {
    return requiredSeries(
      await request<WireSeries>('PATCH', `/v1/me/planned-series/${id}`, {
        json: seriesPatchBody(body),
      }),
    );
  },
  /** DELETE /v1/me/planned-series/{id} — cancel from today. */
  async cancelSeries(id: number): Promise<void> {
    await request<unknown>('DELETE', `/v1/me/planned-series/${id}`);
  },
  /** POST /v1/me/planned-series/{id}/split. */
  async split(
    id: number,
    body: { fromDate: string; changes: SeriesPatch; exceptions?: 'KEEP' | 'RESET' },
  ): Promise<Series> {
    return requiredSeries(
      await request<WireSeries>('POST', `/v1/me/planned-series/${id}/split`, {
        json: { ...body, changes: seriesPatchBody(body.changes) },
      }),
    );
  },
  /** POST /v1/me/planned-series/{id}/end-from. */
  async endFrom(id: number, body: { fromDate: string }): Promise<void> {
    await request<unknown>('POST', `/v1/me/planned-series/${id}/end-from`, { json: body });
  },
  /** GET /v1/me/planned-series/{id}/exceptions — flat query, never a body. */
  async exceptions(id: number, recurrence?: Partial<RecurrenceRule>): Promise<ExceptionPreview[]> {
    return normalizeExceptionPreviewList(
      await request<WireExceptionPreview[]>(
        'GET',
        `/v1/me/planned-series/${id}/exceptions`,
        recurrence !== undefined ? { query: recurrenceQuery(recurrence) } : {},
      ),
    );
  },
  /** GET /v1/me/planned. */
  async calendar(
    q: {
      from?: string | undefined;
      to?: string | undefined;
      accountId?: number | undefined;
      state?: OccurrenceState | undefined;
    } = {},
  ): Promise<Occurrence[]> {
    return normalizeOccurrenceList(
      await request<WireOccurrence[]>('GET', '/v1/me/planned', {
        query: { from: q.from, to: q.to, accountId: q.accountId, state: q.state },
      }),
    );
  },
  /**
   * GET /v1/me/planned with the window tmm-api used (TMMAPI-0188 U3, a forgiving window): a past
   * `from` is read as today, so `windowFrom` (the `Window-From` header) may start later than asked.
   * Null when not served.
   */
  async calendarWindowed(q: {
    from?: string | undefined;
    to?: string | undefined;
    accountId?: number | undefined;
  }): Promise<ServedOccurrences> {
    const result = await requestMeta<WireOccurrence[] | null>('GET', '/v1/me/planned', {
      query: { from: q.from, to: q.to, accountId: q.accountId },
    });
    return {
      occurrences: normalizeOccurrenceList(result.data ?? []),
      windowFrom: civilOrNull(result.header('Window-From')),
      windowTo: civilOrNull(result.header('Window-To')),
    };
  },
  /** GET /v1/me/planned-series/{id}/timeline. */
  async timeline(id: number, q: { from?: string; to?: string } = {}): Promise<Occurrence[]> {
    return normalizeOccurrenceList(
      await request<WireOccurrence[]>('GET', `/v1/me/planned-series/${id}/timeline`, {
        query: { from: q.from, to: q.to },
      }),
    );
  },
  /** PUT /v1/me/planned-series/{id}/occurrences/{originalOn}. */
  async modifyOccurrence(
    id: number,
    originalOn: string,
    body: { occursOn?: string; amountMinor?: number; payee?: string; note?: string },
  ): Promise<Occurrence> {
    return normalizeOccurrence(
      await request<WireOccurrence>(
        'PUT',
        `/v1/me/planned-series/${id}/occurrences/${originalOn}`,
        { json: body },
      ),
    );
  },
  /** DELETE /v1/me/planned-series/{id}/occurrences/{originalOn}. Opaque; caller refetches. */
  async cancelOccurrence(id: number, originalOn: string): Promise<void> {
    await request<unknown>('DELETE', `/v1/me/planned-series/${id}/occurrences/${originalOn}`);
  },
  /** POST …/restore. */
  async restoreOccurrence(id: number, originalOn: string): Promise<Occurrence> {
    return normalizeOccurrence(
      await request<WireOccurrence>(
        'POST',
        `/v1/me/planned-series/${id}/occurrences/${originalOn}/restore`,
      ),
    );
  },
  /** PUT …/slices. */
  async replaceSlices(
    id: number,
    originalOn: string,
    slices: TargetRequest[],
  ): Promise<Occurrence> {
    return normalizeOccurrence(
      await request<WireOccurrence>(
        'PUT',
        `/v1/me/planned-series/${id}/occurrences/${originalOn}/slices`,
        { json: { slices: slices.map(toWireTarget) } },
      ),
    );
  },
  /** POST …/slices. */
  async upsertSlice(id: number, originalOn: string, slice: TargetRequest): Promise<Occurrence> {
    return normalizeOccurrence(
      await request<WireOccurrence>(
        'POST',
        `/v1/me/planned-series/${id}/occurrences/${originalOn}/slices`,
        { json: toWireTarget(slice) },
      ),
    );
  },
  /** DELETE …/slices/{sliceRef}. */
  async cancelSlice(id: number, originalOn: string, sliceRef: string): Promise<Occurrence> {
    return normalizeOccurrence(
      await request<WireOccurrence>(
        'DELETE',
        `/v1/me/planned-series/${id}/occurrences/${originalOn}/slices/${encodeURIComponent(sliceRef)}`,
      ),
    );
  },
  /** POST …/slices/restore. */
  async restoreSlices(id: number, originalOn: string): Promise<Occurrence> {
    return normalizeOccurrence(
      await request<WireOccurrence>(
        'POST',
        `/v1/me/planned-series/${id}/occurrences/${originalOn}/slices/restore`,
      ),
    );
  },
  /**
   * POST …/match. Body is `{ transactionId }`; the response is `JsonNode` and is not parsed.
   */
  async match(
    id: number,
    originalOn: string,
    transactionId: number,
    idempotencyKey: string,
  ): Promise<void> {
    await request<unknown>('POST', `/v1/me/planned-series/${id}/occurrences/${originalOn}/match`, {
      json: { transactionId },
      idempotencyKey,
    });
  },
  /**
   * POST …/unmatch. Response is `JsonNode` and is not parsed. With `transactionId`, only that line
   * leaves (TMMAPI-0202); without it, every line does (no body, as before).
   */
  async unmatch(
    id: number,
    originalOn: string,
    idempotencyKey: string,
    transactionId?: number,
  ): Promise<void> {
    const body: UnmatchRequest | undefined =
      transactionId !== undefined ? { transactionId } : undefined;
    await request<unknown>(
      'POST',
      `/v1/me/planned-series/${id}/occurrences/${originalOn}/unmatch`,
      body !== undefined ? { json: body, idempotencyKey } : { idempotencyKey },
    );
  },
  /** POST …/close ("that's all", TMMAPI-0202): a `PARTIAL` occurrence closes short. */
  async close(id: number, originalOn: string): Promise<Occurrence> {
    return normalizeOccurrence(
      await request<WireOccurrence>(
        'POST',
        `/v1/me/planned-series/${id}/occurrences/${originalOn}/close`,
      ),
    );
  },
  /** POST …/reopen (TMMAPI-0202): a closed-short occurrence is `PARTIAL` again. */
  async reopen(id: number, originalOn: string): Promise<Occurrence> {
    return normalizeOccurrence(
      await request<WireOccurrence>(
        'POST',
        `/v1/me/planned-series/${id}/occurrences/${originalOn}/reopen`,
      ),
    );
  },
  /** POST …/post-now. Response is `JsonNode` and is not parsed. */
  async postNow(id: number, originalOn: string, idempotencyKey: string): Promise<void> {
    await request<unknown>(
      'POST',
      `/v1/me/planned-series/${id}/occurrences/${originalOn}/post-now`,
      { idempotencyKey },
    );
  },
  /** POST /v1/me/accounts/{id}/coverage. */
  async coverage(
    accountId: number,
    body: { coveredThrough: string; uncoveredPlanned?: 'WIPE' | 'KEEP' },
  ): Promise<CoverageResult> {
    return normalizeCoverage(
      await request<WireCoverage>('POST', `/v1/me/accounts/${accountId}/coverage`, {
        json: body,
      }),
    );
  },
};
